/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useEffect, useRef, useState } from 'react';

type Options = {
  rootMargin?: string;
};

export const useNearViewport = <T extends Element = HTMLDivElement>({
  rootMargin = '200px',
}: Options = {}) => {
  const ref = useRef<T | null>(null);
  // Always start as "not in view" — even during SSR. Initializing to `true` on the
  // server would cause hydration mismatches in consumers that conditionally render
  // based on this flag (e.g. PaypalPayLaterMessaging gates its <Script>) and would
  // emit the deferred markup in SSR HTML, defeating the lazy-load intent.
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    // No IntersectionObserver support → fall back to eager (preserves functionality
    // on legacy browsers). This runs only on the client, so it doesn't affect SSR.
    if (typeof window.IntersectionObserver !== 'function') {
      setIsInView(true);
      return;
    }

    const node = ref.current;
    if (!node) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );
    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [rootMargin]);

  return { ref, isInView };
};
